import apiService from '../apiService';
import { Question, MatchingPair } from '../questionGenerator/types';
import logger from '../utils/logger';

// Helper to extract text from a column item (could be string or MatchingItem object)
const getColumnItemText = (item: unknown): string => {
  if (typeof item === 'string') return item;
  if (item && typeof item === 'object') {
    const obj = item as Record<string, unknown>;
    // Try common text field names
    if (typeof obj.text === 'string') return obj.text;
    if (typeof obj.label === 'string') return obj.label;
    if (typeof obj.value === 'string') return obj.value;
  }
  return '';
};

export interface QuizConfig {
  standard: string;
  questionCount: number;
  mode: 'practice' | 'exam';
  timePerQuestion?: number | null;
  showExplanations?: boolean;
  sectionId?: string | null;
  difficulties?: string[];
  questionTypes?: string[];
  isWarmup?: boolean;
  subStandardIds?: string[];  // Sub-standard IDs for topic-filtered quizzes
  sectionTitle?: string;      // First section title (for display in Focus badge)
  sectionTitles?: string[];   // All section titles including children (for filtering)
}

export interface QuizState {
  questions: Question[];
  currentQuestionIndex: number;
  selectedAnswers: (number | null | MatchingPair[] | string | string[])[];
  temporaryAnswer: number | null | MatchingPair[] | string | string[];
  validatedAnswers: (number | null | MatchingPair[] | string | string[])[];
  startTime: Date;
  questionStartTimes: number[];
  timeRemaining: number | null;
  isComplete: boolean;
  isPaused: boolean;
  config: QuizConfig;
}

export interface QuizAnswer {
  questionId?: string;
  questionText: string;
  questionType: string;
  category?: string;
  difficulty?: string;
  standard: string;
  userAnswer: string | number | boolean | string[] | null;
  correctAnswer: string | number | boolean | string[];
  isCorrect: boolean;
  timeSpent?: number;
  explanation?: string;
  partialCorrectCount?: number;
  totalParts?: number;
}

export interface QuizSubmission {
  answers: QuizAnswer[];
  totalTimeSpent?: number;
  standard?: string;
  mode?: string;
  timeSeconds?: number;
  difficulty?: string | null;  // Quiz difficulty filter: easy, medium, hard, or null for mixed
  isWarmup?: boolean;
  subStandardIds?: string[];  // Sub-standard IDs used for this quiz
  sectionTitle?: string;      // Section title for section-filtered quizzes
}

export interface QuizResult {
  id: string;
  userId: string;
  standard: string;
  mode: string;
  score: number;
  totalQuestions: number;
  percentage: number;
  timeSeconds: number;
  completedAt: Date;
  questionResults?: unknown[];
  points?: number; // Points earned from this quiz (0 in practice mode)
  newAchievements?: Array<{ id: string; name: string; description: string; icon: string; points: number }>;
}

class QuizService {
  private currentState: QuizState | null = null;
  private timers: Map<string, NodeJS.Timeout> = new Map();
  private listeners = new Set<(state: QuizState) => void>();
  private timerBaseline: { startTime: number; endTime: number } | null = null;

  /**
   * Subscribe to quiz state changes (especially timer updates)
   * @param listener Callback function that receives the updated quiz state
   * @returns Unsubscribe function
   */
  subscribe(listener: (state: QuizState) => void): () => void {
    this.listeners.add(listener);

    // Immediately call with current state if quiz is active
    if (this.currentState) {
      listener(this.currentState);
    }

    // Return unsubscribe function
    return () => {
      this.listeners.delete(listener);
    };
  }

  /**
   * Notify all listeners of state change
   * @private
   */
  private notifyListeners(): void {
    if (this.currentState) {
      this.listeners.forEach(listener => {
        try {
          listener({ ...this.currentState! });
        } catch (error) {
          logger.error('Error in quiz state listener', error);
        }
      });
    }
  }

  async startQuiz(config: QuizConfig): Promise<QuizState> {
    try {
      logger.info('Starting quiz', config);

      // Fetch questions from backend
      const questions = await this.fetchQuestions(config);

      // Initialize quiz state
      this.currentState = {
        questions,
        currentQuestionIndex: 0,
        selectedAnswers: new Array(questions.length).fill(null),
        temporaryAnswer: null,
        validatedAnswers: new Array(questions.length).fill(null),
        startTime: new Date(),
        questionStartTimes: [Date.now()],
        timeRemaining: config.timePerQuestion ? config.questionCount * config.timePerQuestion : null,
        isComplete: false,
        isPaused: false,
        config
      };

      // Start timer if exam mode
      if (config.mode === 'exam' && config.timePerQuestion) {
        this.startQuizTimer();
      }

      return this.currentState;
    } catch (error) {
      logger.error('Failed to start quiz', error);
      throw error;
    }
  }

  async fetchQuestions(config: QuizConfig): Promise<Question[]> {
    try {
      const response = await apiService.get('/quizzes/questions', {
        params: {
          standard: config.standard,
          count: config.questionCount,
          difficulties: config.difficulties?.join(','),
          questionTypes: config.questionTypes?.join(','),
          sectionId: config.sectionId,
          isWarmup: config.isWarmup ? 'true' : undefined,
          subStandardIds: config.subStandardIds?.join(','),
          sectionTitles: config.sectionTitles?.join('|||')  // Pass all section titles for filtering
        }
      });

      logger.info('Questions API response:', response);

      if (!response) {
        throw new Error('No data received from questions API');
      }

      // Handle both possible response formats
      // apiService.get returns response.data, so response is already the data
      const questions = (response as any).questions || response;

      if (!Array.isArray(questions)) {
        logger.error('Invalid questions format:', response);
        throw new Error('Invalid questions format received from API');
      }

      // Shuffle options for multiple-choice questions to randomize answer positions
      const shuffledQuestions = questions.map(q => {
        if (q.questionType === 'multiple-choice' && Array.isArray(q.options) && typeof q.correctAnswer === 'number') {
          // Create array of indices and shuffle them
          const indices = q.options.map((_: unknown, i: number) => i);
          for (let i = indices.length - 1; i > 0; i--) {
            const j = Math.floor(Math.random() * (i + 1));
            [indices[i], indices[j]] = [indices[j], indices[i]];
          }

          // Reorder options based on shuffled indices
          const shuffledOptions = indices.map((i: number) => q.options[i]);

          // Find new position of correct answer
          const newCorrectAnswer = indices.indexOf(q.correctAnswer);

          // Also shuffle distractorRationale if present (keys are string indices "0", "1", "2", "3")
          let shuffledDistractorRationale = q.distractorRationale;
          if (q.distractorRationale && typeof q.distractorRationale === 'object') {
            shuffledDistractorRationale = {};
            indices.forEach((oldIndex: number, newIndex: number) => {
              const oldKey = String(oldIndex);
              if (q.distractorRationale[oldKey]) {
                (shuffledDistractorRationale as any)[String(newIndex)] = q.distractorRationale[oldKey];
              }
            });
          }

          return {
            ...q,
            options: shuffledOptions,
            correctAnswer: newCorrectAnswer,
            distractorRationale: shuffledDistractorRationale
          };
        }
        return q;
      });

      return shuffledQuestions;
    } catch (error) {
      logger.error('Failed to fetch questions', error);
      throw error;
    }
  }

  selectAnswer(questionIndex: number, answer: number | MatchingPair[] | string | string[] | null): void {
    if (!this.currentState) {
      throw new Error('No active quiz');
    }

    if (questionIndex < 0 || questionIndex >= this.currentState.questions.length) {
      throw new Error('Invalid question index');
    }

    // Store as temporary answer (not yet validated)
    this.currentState.temporaryAnswer = answer;
    this.currentState.selectedAnswers[questionIndex] = answer;
  }

  validateAnswer(questionIndex: number): void {
    if (!this.currentState) {
      throw new Error('No active quiz');
    }

    if (questionIndex < 0 || questionIndex >= this.currentState.questions.length) {
      throw new Error('Invalid question index');
    }

    // Move answer from temporary to validated
    if (this.currentState.temporaryAnswer !== null) {
      this.currentState.validatedAnswers[questionIndex] = this.currentState.temporaryAnswer;
      this.currentState.temporaryAnswer = null;
    }
  }

  nextQuestion(): boolean {
    if (!this.currentState) {
      throw new Error('No active quiz');
    }

    // In exam mode, auto-validate when moving to next question
    // In practice mode, only move if already validated or no answer given
    if (this.currentState.config.mode === 'exam') {
      this.validateAnswer(this.currentState.currentQuestionIndex);
    }
    // Note: In practice mode, user must explicitly click "Check Answer" to validate

    if (this.currentState.currentQuestionIndex < this.currentState.questions.length - 1) {
      this.currentState.currentQuestionIndex++;
      this.currentState.questionStartTimes.push(Date.now());
      this.currentState.temporaryAnswer = null; // Clear temporary answer for new question
      return true;
    }

    return false;
  }

  previousQuestion(): boolean {
    if (!this.currentState) {
      throw new Error('No active quiz');
    }

    if (this.currentState.currentQuestionIndex > 0) {
      this.currentState.currentQuestionIndex--;
      this.currentState.temporaryAnswer = null; // Clear temporary answer when navigating
      return true;
    }

    return false;
  }

  goToQuestion(index: number): void {
    if (!this.currentState) {
      throw new Error('No active quiz');
    }

    if (index < 0 || index >= this.currentState.questions.length) {
      throw new Error('Invalid question index');
    }

    this.currentState.currentQuestionIndex = index;
  }

  async submitQuiz(): Promise<QuizResult> {
    if (!this.currentState) {
      throw new Error('No active quiz');
    }

    try {
      // Stop timer
      this.stopQuizTimer();

      // Calculate time spent on each question
      const timeSpentPerQuestion = this.calculateTimeSpent();

      // Validate current temporary answer if exists
      if (this.currentState.temporaryAnswer !== null) {
        this.validateAnswer(this.currentState.currentQuestionIndex);
      }

      // Prepare answers - use validatedAnswers instead of selectedAnswers
      const answers: QuizAnswer[] = this.currentState.questions.map((question, index) => {
        const userAnswer = this.currentState!.validatedAnswers[index];
        const isCorrect = this.checkAnswer(question, userAnswer);

        // Calculate partial correctness for multi-part questions
        const partialCorrectness = this.calculatePartialCorrectness(question, userAnswer);

        // For short-answer questions, include AI band score from metadata
        // Note: Use explicit null/undefined check since bandScore of 0 is valid
        const bandScore = question.questionType === 'short-answer' &&
          question.metadata?.bandScore !== undefined && question.metadata?.bandScore !== null
          ? (question.metadata.bandScore as number)
          : undefined;

        // Get AI feedback from metadata for short-answer questions
        const aiFeedback = question.questionType === 'short-answer' &&
          question.metadata?.aiFeedback
          ? (question.metadata.aiFeedback as string)
          : undefined;

        // Get options for question types that need them for display
        let options: unknown = null;
        if (question.questionType === 'multiple-choice') {
          // Include answer choices and distractor rationale for display
          options = {
            choices: question.options || [],
            distractorRationale: question.distractorRationale || null
          };
        } else if (question.questionType === 'true-false') {
          // Include answer choices for display
          options = question.options || null;
        } else if (question.questionType === 'fill-blanks') {
          // Include blanks with acceptable answers
          options = (question as any).blanks || null;
        } else if (question.questionType === 'ranking') {
          // Include the ranking options (steps) - ensure we have an array
          // question.options might be { steps: [...] } or directly [...]
          const rawOptions = question.options;
          if (rawOptions) {
            options = (rawOptions as any).steps && Array.isArray((rawOptions as any).steps)
              ? { steps: (rawOptions as any).steps }
              : Array.isArray(rawOptions)
                ? { steps: rawOptions }
                : rawOptions;
          }
        } else if (question.questionType === 'matching') {
          // Include left/right columns for matching
          options = {
            leftColumn: (question as any).leftColumn,
            rightColumn: (question as any).rightColumn
          };
        } else if (question.questionType === 'calculation') {
          // Include unit, tolerance, and formula/steps for calculation display
          options = {
            unit: (question as any).unit || (question.metadata as any)?.unit || null,
            tolerance: (question as any).tolerance || (question.metadata as any)?.tolerance || 0.01,
            calculationSteps: (question.metadata as any)?.calculationSteps || null,
            formula: (question.metadata as any)?.formula || null
          };
        }

        return {
          questionId: question.id,
          questionText: question.question,
          questionType: question.questionType,
          category: question.category,
          difficulty: question.difficulty,
          standard: this.currentState!.config.standard,
          userAnswer: this.formatAnswer(userAnswer, question) as any,
          correctAnswer: this.formatCorrectAnswer(question) as any,
          isCorrect,
          timeSpent: timeSpentPerQuestion[index],
          explanation: question.explanation,
          partialCorrectCount: partialCorrectness.correct,
          totalParts: partialCorrectness.total,
          bandScore, // Include band score for short-answer questions
          aiFeedback, // Include AI feedback for short-answer questions
          options // Include options for fill-blanks, ranking, matching
        };
      });

      // Calculate total time
      const totalTimeSeconds = Math.floor(
        (Date.now() - this.currentState.startTime.getTime()) / 1000
      );

      // Submit to backend
      // Derive difficulty: if single value selected, use it; otherwise null (mixed)
      const difficulties = this.currentState.config.difficulties;
      const quizDifficulty = difficulties && difficulties.length === 1 ? difficulties[0] : null;

      const submission: QuizSubmission = {
        answers,
        totalTimeSpent: totalTimeSeconds,
        standard: this.currentState.config.standard,
        mode: this.currentState.config.mode,
        timeSeconds: totalTimeSeconds,
        difficulty: quizDifficulty,
        isWarmup: this.currentState.config.isWarmup,
        subStandardIds: this.currentState.config.subStandardIds,
        sectionTitle: this.currentState.config.sectionTitle
      };

      const result = await apiService.post('/quizzes/submit', submission);

      // Mark quiz as complete
      this.currentState.isComplete = true;

      return result as QuizResult;
    } catch (error) {
      logger.error('Failed to submit quiz', error);
      throw error;
    }
  }

  checkAnswer(question: Question, userAnswer: unknown): boolean {
    switch (question.questionType) {
      case 'multiple-choice':
        // Normalize correctAnswer to number (same logic as QuestionPreviews.tsx)
        const correctAnswerIndex = typeof question.correctAnswer === 'number'
          ? question.correctAnswer
          : parseInt(question.correctAnswer as string);
        return userAnswer === correctAnswerIndex;

      case 'true-false':
        // Normalize correctAnswer to boolean (same logic as QuestionPreviews.tsx)
        const correctAnswer = typeof question.correctAnswer === 'boolean'
          ? question.correctAnswer
          : question.correctAnswer === 'true';
        return userAnswer === correctAnswer;

      case 'matching':
        if (!userAnswer || !Array.isArray(userAnswer)) return false;

        // Get columns for converting IDs to content
        const leftColumn = question.leftColumn || (question.options as any)?.leftColumn || [];
        const rightColumn = question.rightColumn || (question.options as any)?.rightColumn || [];

        // Normalize correctPairs to array format
        // Handle object format { "0": 1, "1": 3, ... } from recent AI-generated questions
        type CorrectPairFormat = [number, number] | { left?: string; right?: string; term?: string; definition?: string; leftId?: string; rightId?: string };
        let correctPairs: CorrectPairFormat[] = [];
        const rawCorrectPairs = question.correctPairs;
        if (Array.isArray(rawCorrectPairs)) {
          correctPairs = rawCorrectPairs as CorrectPairFormat[];
        } else if (rawCorrectPairs && typeof rawCorrectPairs === 'object') {
          // Convert object format to array format [[leftIdx, rightIdx], ...]
          correctPairs = Object.entries(rawCorrectPairs).map(([leftIdx, rightIdx]): [number, number] => [
            parseInt(leftIdx, 10),
            rightIdx as number
          ]);
        }

        // Handle normalized ID format (leftId: "left-0", rightId: "right-1")
        // Convert to actual content for comparison
        const normalizedUserAnswer = userAnswer.map((pair: MatchingPair) => {
          // If already in content format (left/right), use as-is
          if (pair.left !== undefined && pair.right !== undefined &&
              !pair.left.startsWith('left-') && !pair.right.startsWith('right-')) {
            return { left: pair.left, right: pair.right };
          }

          // Convert from ID format to content format
          const leftId = pair.leftId || pair.left || '';
          const rightId = pair.rightId || pair.right || '';

          // Extract indices from IDs like "left-0" -> 0
          const leftIdx = parseInt(leftId.replace('left-', ''), 10);
          const rightIdx = parseInt(rightId.replace('right-', ''), 10);

          return {
            left: leftColumn[leftIdx] || '',
            right: rightColumn[rightIdx] || ''
          };
        });

        // Normalize correctPairs to content format as well
        const normalizedCorrectPairs = correctPairs.map((pair: CorrectPairFormat) => {
          // Handle array format [leftIdx, rightIdx]
          if (Array.isArray(pair)) {
            const leftIdx = pair[0];
            const rightIdx = pair[1];
            return {
              left: leftColumn[leftIdx] || '',
              right: rightColumn[rightIdx] || ''
            };
          }

          // If already in content format, use as-is
          if (pair.left !== undefined && pair.right !== undefined &&
              !pair.left.startsWith('left-') && !pair.right.startsWith('right-')) {
            return { left: pair.left, right: pair.right };
          }
          if (pair.term !== undefined && pair.definition !== undefined) {
            return { left: pair.term, right: pair.definition };
          }

          // Convert from ID format to content format
          const leftId = pair.leftId || pair.left || '';
          const rightId = pair.rightId || pair.right || '';

          const leftIdx = parseInt(leftId.replace('left-', ''), 10);
          const rightIdx = parseInt(rightId.replace('right-', ''), 10);

          return {
            left: leftColumn[leftIdx] || '',
            right: rightColumn[rightIdx] || ''
          };
        });

        // Now compare content
        const result = normalizedUserAnswer.length === normalizedCorrectPairs.length &&
          normalizedUserAnswer.every((pair: { left: string; right: string }) =>
            normalizedCorrectPairs.some((correct: { left: string; right: string }) =>
              correct.left === pair.left && correct.right === pair.right
            )
          );

        return result;

      case 'fill-blanks':
        if (!userAnswer || !Array.isArray(userAnswer)) return false;
        const correctBlanks = question.correctBlanks || [];

        // Get blanks array which may contain acceptableAnswers
        const blanks = (question.blanks || question.correctAnswer || []) as Array<{ acceptableAnswers?: string[] }>;

        return userAnswer.length === correctBlanks.length &&
          userAnswer.every((answer: string, index: number) => {
            const userAns = answer?.toLowerCase().trim();
            const correctAns = correctBlanks[index]?.toLowerCase().trim();

            // Check if matches the main correct answer
            if (userAns === correctAns) return true;

            // Check if matches any acceptable answer
            const blankData = blanks[index];
            if (blankData && Array.isArray(blankData.acceptableAnswers)) {
              return blankData.acceptableAnswers.some(
                (acceptable: string) => userAns === acceptable.toLowerCase().trim()
              );
            }

            return false;
          });

      case 'ranking':
        if (!userAnswer || !Array.isArray(userAnswer)) return false;

        // Get the items array from metadata
        type RankingMetadataItem = { text: string; correctOrder: number };
        const metadata = (question.metadata || {}) as { items?: RankingMetadataItem[] };
        const items = metadata.items || [];

        // Build the correct order of texts
        const correctOrderTexts = items
          .sort((a: RankingMetadataItem, b: RankingMetadataItem) => a.correctOrder - b.correctOrder)
          .map((item: RankingMetadataItem) => item.text);

        // Compare user's order with correct order
        return userAnswer.length === correctOrderTexts.length &&
          userAnswer.every((item: string, index: number) =>
            item === correctOrderTexts[index]
          );

      case 'calculation':
        if (userAnswer === null || userAnswer === undefined) return false;
        const userNum = parseFloat(userAnswer.toString());
        // correctAnswer might be an object { value: X } or just a number
        const correctAnswerValue = typeof question.correctAnswer === 'object' && question.correctAnswer !== null
          ? (question.correctAnswer as { value?: number }).value
          : question.correctAnswer;
        const correctNum = parseFloat(correctAnswerValue?.toString() || '0');
        const tolerance = question.tolerance || 0.01;
        // Calculate tolerance as percentage of correct answer
        return Math.abs(userNum - correctNum) <= (correctNum * tolerance);

      case 'short-answer':
        // Short answers are evaluated by AI, so we don't check here
        return false;

      default:
        return false;
    }
  }

  /**
   * Calculate partial correctness for multi-part questions
   * Returns { correct: number of correct parts, total: total number of parts }
   */
  calculatePartialCorrectness(question: Question, userAnswer: unknown): { correct: number; total: number } {
    switch (question.questionType) {
      case 'matching':
        // Normalize correctPairs from object format { "0": 1, "1": 3, ... } to array format
        type PartialCorrectPairFormat = [number, number] | { left?: string; right?: string; term?: string; definition?: string; leftId?: string; rightId?: string };
        let correctPairsForPartial: PartialCorrectPairFormat[] = [];
        const rawPairs = question.correctPairs;
        if (Array.isArray(rawPairs)) {
          correctPairsForPartial = rawPairs as PartialCorrectPairFormat[];
        } else if (rawPairs && typeof rawPairs === 'object') {
          correctPairsForPartial = Object.entries(rawPairs).map(([leftIdx, rightIdx]): [number, number] => [
            parseInt(leftIdx, 10),
            rightIdx as number
          ]);
        }

        if (!userAnswer || !Array.isArray(userAnswer)) {
          return { correct: 0, total: correctPairsForPartial.length || 0 };
        }

        const opts = (!Array.isArray(question.options) ? question.options : undefined) as Record<string, unknown> | undefined;
        const leftColumn = question.leftColumn || (opts?.leftColumn as string[]) || [];
        const rightColumn = question.rightColumn || (opts?.rightColumn as string[]) || [];

        // Normalize user answers to content format
        const normalizedUserAnswer = userAnswer.map((pair: MatchingPair) => {
          if (pair.left !== undefined && pair.right !== undefined &&
              !pair.left.startsWith('left-') && !pair.right.startsWith('right-')) {
            return { left: pair.left, right: pair.right };
          }

          const leftId = pair.leftId || pair.left || '';
          const rightId = pair.rightId || pair.right || '';
          const leftIdx = parseInt(leftId.replace('left-', ''), 10);
          const rightIdx = parseInt(rightId.replace('right-', ''), 10);

          return {
            left: leftColumn[leftIdx] || '',
            right: rightColumn[rightIdx] || ''
          };
        });

        // Normalize correct pairs to content format
        const normalizedCorrectPairs = correctPairsForPartial.map((pair: PartialCorrectPairFormat) => {
          if (Array.isArray(pair)) {
            return {
              left: leftColumn[pair[0]] || '',
              right: rightColumn[pair[1]] || ''
            };
          }
          if (pair.leftId && pair.rightId) {
            const leftIdx = parseInt(pair.leftId.replace('left-', ''), 10);
            const rightIdx = parseInt(pair.rightId.replace('right-', ''), 10);
            return {
              left: leftColumn[leftIdx] || '',
              right: rightColumn[rightIdx] || ''
            };
          }
          return { left: pair.left || '', right: pair.right || '' };
        });

        // Count how many pairs match
        type NormalizedPair = { left: string; right: string };
        const correctCount = normalizedUserAnswer.filter((userPair: NormalizedPair) =>
          normalizedCorrectPairs.some((correctPair: NormalizedPair) =>
            userPair.left === correctPair.left && userPair.right === correctPair.right
          )
        ).length;

        return { correct: correctCount, total: correctPairsForPartial.length };

      case 'fill-blanks':
        if (!userAnswer || !Array.isArray(userAnswer)) {
          return { correct: 0, total: question.correctBlanks?.length || 0 };
        }

        const correctBlanks = question.correctBlanks || [];
        const blanks = (question.blanks || question.correctAnswer || []) as Array<{ acceptableAnswers?: string[] }>;

        const correctBlanksCount = userAnswer.filter((answer: string, index: number) => {
          const userAns = answer?.toLowerCase().trim();
          const correctAns = correctBlanks[index]?.toLowerCase().trim();

          // Check main correct answer
          if (userAns === correctAns) return true;

          // Check acceptable answers
          const blankData = blanks[index];
          if (blankData && Array.isArray(blankData.acceptableAnswers)) {
            return blankData.acceptableAnswers.some(
              (acceptable: string) => userAns === acceptable.toLowerCase().trim()
            );
          }

          return false;
        }).length;

        return { correct: correctBlanksCount, total: correctBlanks.length };

      case 'ranking':
        type RankingItem = { text: string; correctOrder: number };
        if (!userAnswer || !Array.isArray(userAnswer)) {
          const metadata = (question.metadata || {}) as { items?: RankingItem[] };
          const itemsLength = metadata?.items?.length || question.correctOrder?.length || 0;
          return { correct: 0, total: itemsLength };
        }

        const rankingMetadata = (question.metadata || {}) as { items?: RankingItem[] };
        const itemsArray = rankingMetadata.items || [];
        const correctOrder = itemsArray
          .sort((a: RankingItem, b: RankingItem) => a.correctOrder - b.correctOrder)
          .map((item: RankingItem) => item.text);

        const correctPositions = (userAnswer as string[]).filter((item, index) =>
          item === correctOrder[index]
        ).length;

        return { correct: correctPositions, total: correctOrder.length };

      case 'calculation':
        // Calculation is binary - either within tolerance or not
        const isCorrect = this.checkAnswer(question, userAnswer);
        return { correct: isCorrect ? 1 : 0, total: 1 };

      default:
        // Other question types don't support partial credit
        return { correct: 0, total: 1 };
    }
  }

  private formatAnswer(answer: unknown, question?: Question): unknown {
    if (answer === null || answer === undefined) return null;

    if (Array.isArray(answer)) {
      if (answer.length > 0 && typeof answer[0] === 'object') {
        const firstItem = answer[0] as Record<string, unknown>;

        // Check if it's matching pairs (could have left/right or leftId/rightId)
        if ('left' in firstItem || 'leftId' in firstItem) {
          // Get leftColumn and rightColumn for ID to text conversion
          const leftColumn = (question?.leftColumn || []) as unknown[];
          const rightColumn = (question?.rightColumn || []) as unknown[];

          return (answer as Record<string, unknown>[]).map((pair: Record<string, unknown>) => {
            let leftText = pair.left as string | undefined;
            let rightText = pair.right as string | undefined;

            // Convert IDs to text if needed
            const leftId = pair.leftId as string | undefined;
            const rightId = pair.rightId as string | undefined;
            if (leftId && leftId.startsWith('left-')) {
              const leftIndex = parseInt(leftId.replace('left-', ''));
              // Extract text from MatchingItem object if needed
              leftText = getColumnItemText(leftColumn[leftIndex]) || leftId;
            }
            if (rightId && rightId.startsWith('right-')) {
              const rightIndex = parseInt(rightId.replace('right-', ''));
              // Extract text from MatchingItem object if needed
              rightText = getColumnItemText(rightColumn[rightIndex]) || rightId;
            }

            return `${leftText || leftId || '?'}:${rightText || rightId || '?'}`;
          }).join(';');
        }
      }
      // Array of strings
      return (answer as unknown[]).join(';');
    }

    return answer;
  }

  private formatCorrectAnswer(question: Question): unknown {
    switch (question.questionType) {
      case 'matching':
        // Get leftColumn and rightColumn for text lookup
        const leftColumn = (question.leftColumn || []) as unknown[];
        const rightColumn = (question.rightColumn || []) as unknown[];

        // Handle both array format and object format for correctPairs
        const rawPairs = question.correctPairs;
        if (!rawPairs) return '';

        type MatchPairFormat = [number, number] | { left?: string; right?: string; term?: string; definition?: string };
        let pairsArray: MatchPairFormat[];
        if (Array.isArray(rawPairs)) {
          pairsArray = rawPairs as MatchPairFormat[];
        } else if (typeof rawPairs === 'object') {
          // Convert object format { "0": 1, "1": 3, ... } to array with actual text
          pairsArray = Object.entries(rawPairs).map(([leftIdx, rightIdx]): { left: string; right: string } => ({
            left: getColumnItemText(leftColumn[parseInt(leftIdx)]) || `Item ${parseInt(leftIdx) + 1}`,
            right: getColumnItemText(rightColumn[rightIdx as number]) || `Match ${(rightIdx as number) + 1}`
          }));
        } else {
          return '';
        }

        return pairsArray.map((pair: MatchPairFormat) => {
          if (Array.isArray(pair)) {
            // Array format [leftIndex, rightIndex]
            const leftText = getColumnItemText(leftColumn[pair[0]]) || `Item ${pair[0] + 1}`;
            const rightText = getColumnItemText(rightColumn[pair[1]]) || `Match ${pair[1] + 1}`;
            return `${leftText}:${rightText}`;
          }
          // Object format with left/right or term/definition
          return `${pair.left || pair.term || ''}:${pair.right || pair.definition || ''}`;
        }).join(';') || '';

      case 'fill-blanks':
        // Include acceptable alternatives in the format: "answer (alt1, alt2)"
        const blanks = ((question.blanks || []) as Array<{ correctAnswer?: string; acceptableAnswers?: string[] }>);
        const correctBlanks = question.correctBlanks || [];

        if (blanks.length > 0 && blanks[0]?.correctAnswer) {
          // Use full blanks data with alternatives
          return blanks.map((blank) => {
            const main = blank.correctAnswer || '';
            const alts = blank.acceptableAnswers || [];
            if (alts.length > 0) {
              return `${main} (${alts.join(', ')})`;
            }
            return main;
          }).join(';') || '';
        }
        // Fallback to simple correctBlanks
        return correctBlanks.join(';') || '';

      case 'ranking':
        // Store actual step text instead of indices so it can be displayed without options
        const rankingSteps = (question.options as any)?.steps || question.options || [];
        if (question.correctOrder && Array.isArray(rankingSteps) && rankingSteps.length > 0) {
          return question.correctOrder.map(idx => {
            // correctOrder is typically 1-based, convert to 0-based array index
            const arrayIdx = typeof idx === 'number' ? idx - 1 : parseInt(String(idx)) - 1;
            return rankingSteps[arrayIdx] || `Step ${idx}`;
          }).join(';');
        }
        return question.correctOrder?.join(';') || '';

      case 'short-answer':
        // For short-answer, the expected answer is stored in expectedAnswer field
        return (question as any).expectedAnswer || question.correctAnswer || '';

      default:
        return question.correctAnswer;
    }
  }

  private calculateTimeSpent(): number[] {
    if (!this.currentState) return [];

    const timeSpent: number[] = [];
    const startTimes = this.currentState.questionStartTimes;

    for (let i = 0; i < this.currentState.questions.length; i++) {
      if (i < startTimes.length - 1) {
        // Time between this question and the next
        timeSpent.push(Math.floor((startTimes[i + 1] - startTimes[i]) / 1000));
      } else if (i === startTimes.length - 1) {
        // Time for the current/last viewed question
        timeSpent.push(Math.floor((Date.now() - startTimes[i]) / 1000));
      } else {
        // Questions not yet viewed
        timeSpent.push(0);
      }
    }

    return timeSpent;
  }

  private startQuizTimer(): void {
    if (!this.currentState || !this.currentState.timeRemaining) return;

    // Set baseline anchor - fixed point in time to calculate from
    const now = Date.now();
    this.timerBaseline = {
      startTime: now,
      endTime: now + (this.currentState.timeRemaining * 1000)
    };

    const tick = () => {
      if (!this.currentState || !this.timerBaseline) return;

      // Calculate remaining time from baseline (no decrementing - always accurate)
      const now = Date.now();
      const remaining = Math.max(0, this.timerBaseline.endTime - now);
      const remainingSeconds = Math.ceil(remaining / 1000);

      // Update state with calculated value
      this.currentState.timeRemaining = remainingSeconds;

      // Notify React of timer update
      this.notifyListeners();

      // Check if time is up
      if (remainingSeconds <= 0) {
        this.stopQuizTimer();
        // Auto-submit quiz when time runs out
        this.submitQuiz().catch(error => {
          logger.error('Failed to auto-submit quiz', error);
        });
        return;
      }

      // Schedule next tick (self-calling setTimeout)
      this.timers.set('quiz', setTimeout(tick, 1000));
    };

    // Start ticking
    this.timers.set('quiz', setTimeout(tick, 1000));
  }

  private stopQuizTimer(): void {
    const timerId = this.timers.get('quiz');
    if (timerId) {
      clearTimeout(timerId);
      this.timers.delete('quiz');
    }
    this.timerBaseline = null;
  }

  pauseQuiz(): void {
    if (!this.currentState) {
      throw new Error('No active quiz');
    }

    // Calculate and freeze remaining time at pause moment
    if (this.timerBaseline && this.currentState.timeRemaining !== null) {
      const now = Date.now();
      const remaining = Math.max(0, this.timerBaseline.endTime - now);
      this.currentState.timeRemaining = Math.ceil(remaining / 1000);
    }

    this.currentState.isPaused = true;
    this.stopQuizTimer(); // Clears timer and baseline
  }

  resumeQuiz(): void {
    if (!this.currentState) {
      throw new Error('No active quiz');
    }

    this.currentState.isPaused = false;
    if (this.currentState.config.mode === 'exam' && this.currentState.timeRemaining) {
      this.startQuizTimer(); // Recreates baseline from current timeRemaining
    }
  }

  getState(): QuizState | null {
    return this.currentState;
  }

  clearQuiz(): void {
    this.stopQuizTimer();
    this.currentState = null;
  }

  // Utility methods for getting quiz statistics
  getProgress(): { answered: number; total: number; percentage: number; validated: number } {
    if (!this.currentState) {
      return { answered: 0, total: 0, percentage: 0, validated: 0 };
    }

    const answered = this.currentState.selectedAnswers.filter(a => a !== null).length;
    const validated = this.currentState.validatedAnswers.filter(a => a !== null).length;
    const total = this.currentState.questions.length;
    const percentage = total > 0 ? Math.round((validated / total) * 100) : 0;

    return { answered, total, percentage, validated };
  }

  isQuestionAnswered(index: number): boolean {
    if (!this.currentState) return false;
    // Check if the current question has a temporary answer or if it's been selected
    if (index === this.currentState.currentQuestionIndex) {
      return this.currentState.temporaryAnswer !== null || this.currentState.selectedAnswers[index] !== null;
    }
    return this.currentState.selectedAnswers[index] !== null;
  }

  isQuestionValidated(index: number): boolean {
    if (!this.currentState) return false;
    return this.currentState.validatedAnswers[index] !== null;
  }

  /**
   * Store AI evaluation band score and feedback in question metadata
   * This is used for proportional scoring of short-answer questions
   */
  setQuestionBandScore(questionIndex: number, bandScore: number, aiFeedback?: string): void {
    if (!this.currentState) {
      logger.warn('Cannot set band score: No quiz state');
      return;
    }

    if (questionIndex < 0 || questionIndex >= this.currentState.questions.length) {
      logger.warn(`Cannot set band score: Invalid question index ${questionIndex}`);
      return;
    }

    const question = this.currentState.questions[questionIndex];

    // Initialize metadata if it doesn't exist
    if (!question.metadata) {
      question.metadata = {};
    }

    // Store the band score and feedback
    question.metadata.bandScore = bandScore;
    if (aiFeedback) {
      question.metadata.aiFeedback = aiFeedback;
    }

    logger.info(`Stored band score ${bandScore} for question ${questionIndex + 1}`);
  }

  /**
   * Validate the current question's answer (for practice mode two-step flow)
   * Moves temporaryAnswer to validatedAnswers
   */
  validateCurrentAnswer(): boolean {
    if (!this.currentState) {
      return false;
    }

    const currentIndex = this.currentState.currentQuestionIndex;

    // Use temporaryAnswer if available, otherwise use selectedAnswers
    const answerToValidate = this.currentState.temporaryAnswer !== null
      ? this.currentState.temporaryAnswer
      : this.currentState.selectedAnswers[currentIndex];

    if (answerToValidate === null) {
      return false;
    }

    // Move answer to validated
    this.currentState.validatedAnswers[currentIndex] = answerToValidate;
    this.currentState.selectedAnswers[currentIndex] = answerToValidate;
    this.currentState.temporaryAnswer = answerToValidate;

    return true;
  }

  hasTemporaryAnswer(): boolean {
    if (!this.currentState) return false;
    return this.currentState.temporaryAnswer !== null;
  }

  getCurrentQuestion(): Question | null {
    if (!this.currentState) return null;
    return this.currentState.questions[this.currentState.currentQuestionIndex];
  }

  getCurrentAnswer(): unknown {
    if (!this.currentState) return null;
    return this.currentState.selectedAnswers[this.currentState.currentQuestionIndex];
  }

  getTimeSpentOnQuestion(index: number): number {
    if (!this.currentState || index < 0 || index >= this.currentState.questionStartTimes.length) {
      return 0;
    }

    const startTime = this.currentState.questionStartTimes[index];
    let endTime: number;

    // If it's the current question and not the last one, calculate time up to now
    if (index === this.currentState.currentQuestionIndex) {
      endTime = Date.now();
    } else if (index < this.currentState.questionStartTimes.length - 1) {
      // Use the start time of the next question as the end time
      endTime = this.currentState.questionStartTimes[index + 1];
    } else {
      // For the last question, use current time
      endTime = Date.now();
    }

    return Math.floor((endTime - startTime) / 1000); // Return in seconds
  }
}

export default new QuizService();